import { debounce } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import { Component } from 'react';

import { Select } from '@alkem/react-ui-select';

import './autocomplete.scss';

const AUTOCOMPLETE_ADD_OPTION_KEY = 'autocomplete-add-option';

/**
 * Main Autocomplete abstract component
 *
 * inherit and implement getList method (use this.updateList when list is resolved)
 *
 * @props
 *   id: html unique id of the select
 *   value: if defined at instanciation, will be the default selected value
 *   multiple: if true multiple input is possible
 *   disabled: if true Select will appear disabled
 *   excludeList: blacklist options according to keys
 *   onSelect: triggered upon selection of an option
 *   onUnselect: triggered when deleting an option
 *   options: list of options, object { key: , label: , value: } passed on to the select component
 *   placeholder: --
 *   searchOnClick: if true, triggers the getList upon clicking
 *   onAdd: make it "addable": meaning a "add missing <user input>" option will be concatened
 *          the function is triggered when selecting the "add missing <>" option
 *   addMissingOptionLabel: label to replace "add missing <>"
 *   debouncedTime: indicates the amount in ms to wait before triggering the search
 */
export class AutocompleteLegacyV1 extends Component<{
  id: string;
  value: any[];
  className?: string;
  multiple?: boolean;
  disabled?: boolean;
  excludeList?: any[];
  onSelect?: Function;
  onUnselect?: Function;
  options?: any;
  placeholder?: string;
  searchOnClick?: boolean;
  onAdd?: Function;
  addMissingOptionLabel?: string;
  debounceTime?: number;
}> {
  static propTypes = {
    className: PropTypes.string,
    id: PropTypes.string.isRequired,
    value: PropTypes.array,
    multiple: PropTypes.bool,
    disabled: PropTypes.bool,
    excludeList: PropTypes.array,
    onSelect: PropTypes.func,
    onUnselect: PropTypes.func,
    options: PropTypes.object,
    placeholder: PropTypes.string,
    searchOnClick: PropTypes.bool,
    onAdd: PropTypes.func,
    addMissingOptionLabel: PropTypes.string,
    debounceTime: PropTypes.number,
  };

  static defaultProps = {
    className: 'Autocomplete',
    multiple: false,
    disabled: false,
    excludeList: [],
    options: {},
    placeholder: '',
    searchOnClick: false,
    addMissingOptionLabel: 'Add missing',
    debounceTime: 150,
  };

  state: {
    search: string | null;
    list: any[];
    listPromise: (Promise<any> & { cancel?: Function }) | null;
  } = {
    search: null,
    list: [],
    listPromise: null,
  };

  emptyArr = [];

  getList(search: string): any {
    throw new Error(
      `AutocompleteLegacyV1.getList(${search}) method must be implemented`
    );
  }

  search: (query?: string) => void = debounce((currentValue) => {
    if (this.state.listPromise && this.state.listPromise.cancel) {
      this.state.listPromise.cancel();
    }
    if (currentValue || this.props.searchOnClick) {
      this.setState({
        search: currentValue,
        listPromise: this.getList(currentValue || ''),
      });
    } else {
      this.setState({ list: this.emptyArr });
    }
  }, this.props.debounceTime);

  getSelection = memoize((value) => {
    if (!value || !value.length) {
      return this.emptyArr;
    }
    return value.map((item) => ({ item, label: item.label }));
  });

  formatOptions = memoize((options, search) => {
    const { onAdd, addMissingOptionLabel } = this.props;

    if (
      !onAdd ||
      !(
        typeof search === 'string' &&
        search.length > 0 &&
        this.hasNoExactMatch(search, options)
      )
    ) {
      return options;
    }

    return [
      ...options,
      {
        key: AUTOCOMPLETE_ADD_OPTION_KEY,
        value: { search },
        label: (
          <span>
            <em> {addMissingOptionLabel} </em>
            &quot;
            {search}
            &quot;
          </span>
        ),
      },
    ];
  });

  onSelect = (data) => {
    const { onSelect, onAdd } = this.props;
    if (
      data.key === AUTOCOMPLETE_ADD_OPTION_KEY &&
      onAdd &&
      typeof onAdd === 'function'
    ) {
      // if "add missing <option>" is selected, triggers onAdd
      onAdd(data.value.search);
    } else if (onSelect && typeof onSelect === 'function') {
      onSelect(data);
    }
    this.setState({ search: null });
  };

  onFocus = () => {
    if (this.props.searchOnClick) {
      this.search();
    }
  };

  onUnselect = (data) => {
    const { onUnselect } = this.props;
    if (onUnselect && typeof onUnselect === 'function') {
      onUnselect(data);
    }
  };

  hasNoExactMatch(search, list) {
    if (typeof search !== 'string') {
      return true;
    }
    const _search = search.trim().toLowerCase();

    return !list.some(({ label }) => label.toLowerCase() === _search);
  }

  updateList(list) {
    const excludeListIds = this.props.excludeList?.map((e) => e.key) || [];
    const newList = list.filter(
      (option) => excludeListIds.indexOf(option.key) === -1
    );
    this.setState({ list: newList });
  }

  render() {
    const { className, multiple, disabled, options, placeholder, id, value } =
      this.props;
    const { list, search } = this.state;

    return (
      <div className={className} onClick={this.onFocus}>
        <Select
          id={`select-ac-${id}`}
          onValueAdd={this.onSelect}
          onValueDelete={this.onUnselect}
          values={this.getSelection(value)}
          placeholder={placeholder}
          delegateSearch={this.search}
          multiple={multiple}
          disabled={disabled}
          options={this.formatOptions(list, search)}
          extraOptions={options}
          inputable
        />
      </div>
    );
  }
}
